/**
 * useAuth Hook - Manages authentication state and operations
 * Handles Cognito authentication flow with token management
 * Integrates with SessionManager for user profile synchronization
 */

import { useState, useEffect, useCallback } from 'react';
import { storageService, STORAGE_KEYS } from '../services/storage/StorageService';
import { sessionManager } from '../services/user/SessionManager';
import type { User, AuthTokens, JWTPayload } from '../types/auth';

/**
 * Cognito configuration from environment
 */
const COGNITO_CONFIG = {
  domain: import.meta.env.VITE_COGNITO_DOMAIN || 'us-west-2diyeymilb.auth.us-west-2.amazoncognito.com',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '6jsljesdvg7748s50lg5ucdi43',
  redirectUri: import.meta.env.VITE_REDIRECT_URI || 'https://www.8bitcomposer.com/',
  logoutUri: import.meta.env.VITE_REDIRECT_URI || 'https://www.8bitcomposer.com/',
};

/**
 * Return type for useAuth hook
 */
export interface UseAuthReturn {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** User information */
  user: User | null;
  /** ID token */
  idToken: string | null;
  /** Access token */
  accessToken: string | null;
  /** Whether authentication is loading */
  isLoading: boolean;
  /** Sign in (redirect to Cognito) */
  signIn: () => void;
  /** Sign out */
  signOut: () => void;
  /** Refresh authentication status */
  refreshAuth: () => void;
  /** Session sync error */
  syncError: Error | null;
}

/**
 * Parse tokens from URL hash
 * Cognito returns tokens in URL hash after successful authentication
 * 
 * @returns Parsed tokens or null
 */
function parseTokensFromHash(): AuthTokens | null {
  const hash = window.location.hash.substring(1);
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const idToken = params.get('id_token');
  const accessToken = params.get('access_token');

  if (!idToken || !accessToken) return null;

  const tokenType = params.get('token_type');
  const expiresIn = params.get('expires_in');
  
  return {
    id_token: idToken,
    access_token: accessToken,
    ...(tokenType && { token_type: tokenType }),
    ...(expiresIn && { expires_in: parseInt(expiresIn) }),
  };
}

/**
 * Decode JWT token
 * 
 * @param token - JWT token string
 * @returns Decoded payload or null
 */
function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    if (!payload) return null;
    
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('[useAuth] Failed to decode JWT', error);
    return null;
  }
}

/**
 * Extract user info from JWT payload
 * 
 * @param payload - JWT payload
 * @returns User object
 */
function extractUser(payload: JWTPayload): User {
  return {
    sub: payload.sub,
    email: payload.email,
    name: payload.name || payload.email,
    ...(payload.given_name && { given_name: payload.given_name }),
    ...(payload.family_name && { family_name: payload.family_name }),
    ...(payload.picture && { picture: payload.picture }),
    email_verified: payload.email_verified,
  };
}

/**
 * Check if token is expired
 * 
 * @param payload - JWT payload
 * @returns True if expired
 */
function isTokenExpired(payload: JWTPayload): boolean {
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
}

/**
 * Custom hook for managing authentication
 * 
 * @returns Authentication state and control methods
 */
export function useAuth(): UseAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [syncError, setSyncError] = useState<Error | null>(null);

  /**
   * Load authentication state from storage or URL
   * Integrates with SessionManager to sync user profile
   */
  const loadAuthState = useCallback(async () => {
    try {
      // First, check for tokens in URL hash (after redirect from Cognito)
      const tokensFromHash = parseTokensFromHash();
      
      if (tokensFromHash) {
        // Store tokens
        storageService.setItem(STORAGE_KEYS.ID_TOKEN, tokensFromHash.id_token);
        storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokensFromHash.access_token);
        
        // Clear hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        
        // Decode and validate token
        const payload = decodeJWT(tokensFromHash.id_token);
        if (payload && !isTokenExpired(payload)) {
          const userInfo = extractUser(payload);
          setUser(userInfo);
          setIdToken(tokensFromHash.id_token);
          setAccessToken(tokensFromHash.access_token);
          setIsAuthenticated(true);
          
          // Sync user profile with backend after successful authentication
          try {
            await sessionManager.syncUserProfile(tokensFromHash.id_token);
            setSyncError(null);
          } catch (syncErr) {
            console.error('[useAuth] Failed to sync user profile after sign-in', syncErr);
            setSyncError(syncErr as Error);
            // Don't block authentication on sync failure
          }
          
          setIsLoading(false);
          return;
        }
      }

      // Check for stored tokens
      const storedIdToken = storageService.getItem(STORAGE_KEYS.ID_TOKEN);
      const storedAccessToken = storageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (storedIdToken && storedAccessToken) {
        // Validate stored token
        const payload = decodeJWT(storedIdToken);
        
        if (payload && !isTokenExpired(payload)) {
          const userInfo = extractUser(payload);
          setUser(userInfo);
          setIdToken(storedIdToken);
          setAccessToken(storedAccessToken);
          setIsAuthenticated(true);
          
          // Initialize session on app load
          try {
            await sessionManager.initializeSession();
            setSyncError(null);
          } catch (syncErr) {
            console.error('[useAuth] Failed to initialize session on app load', syncErr);
            setSyncError(syncErr as Error);
            // Don't block authentication on sync failure
          }
        } else {
          // Token expired, clear storage and session
          storageService.removeItem(STORAGE_KEYS.ID_TOKEN);
          storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          sessionManager.clearSession();
          setIsAuthenticated(false);
          setUser(null);
          setIdToken(null);
          setAccessToken(null);
          setSyncError(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setIdToken(null);
        setAccessToken(null);
        setSyncError(null);
      }
    } catch (error) {
      console.error('[useAuth] Failed to load auth state', error);
      setIsAuthenticated(false);
      setUser(null);
      setIdToken(null);
      setAccessToken(null);
      setSyncError(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    loadAuthState();
  }, [loadAuthState]);

  /**
   * Sign in - redirect to Cognito
   */
  const signIn = useCallback(() => {
    const authUrl = `https://${COGNITO_CONFIG.domain}/oauth2/authorize?` +
      `client_id=${COGNITO_CONFIG.clientId}&` +
      `response_type=token&` +
      `scope=email+openid+profile&` +
      `redirect_uri=${encodeURIComponent(COGNITO_CONFIG.redirectUri)}`;
    
    window.location.href = authUrl;
  }, []);

  /**
   * Sign out - clear tokens, session, and redirect
   */
  const signOut = useCallback(() => {
    // Clear session (includes tokens from storage)
    sessionManager.clearSession();
    
    // Clear state
    setIsAuthenticated(false);
    setUser(null);
    setIdToken(null);
    setAccessToken(null);
    setSyncError(null);

    // Redirect to Cognito logout
    const logoutUrl = `https://${COGNITO_CONFIG.domain}/logout?` +
      `client_id=${COGNITO_CONFIG.clientId}&` +
      `logout_uri=${encodeURIComponent(COGNITO_CONFIG.logoutUri)}`;
    
    window.location.href = logoutUrl;
  }, []);

  /**
   * Refresh authentication status
   */
  const refreshAuth = useCallback(() => {
    setIsLoading(true);
    loadAuthState();
  }, [loadAuthState]);

  return {
    isAuthenticated,
    user,
    idToken,
    accessToken,
    isLoading,
    signIn,
    signOut,
    refreshAuth,
    syncError,
  };
}
